/**
 * Grand Quack Auto — particle-burst system.
 *
 * Pooled, additive `THREE.Points` cloud for one-shot bursts (confetti on
 * combo tier-up, etc.). One mesh, one buffer, one draw call total — zero
 * allocations after init. Particles are recycled FIFO via a free index
 * pointer; expired particles get alpha 0 and zero velocity.
 *
 * Public API: `triggerConfettiBurst(world, position, opts?)`.
 */

import * as THREE from 'three';
import { PALETTE } from '../config';
import type { System, World } from '../types';

const MAX_PARTICLES = 256;
const DEFAULT_LIFE = 1.4;          // seconds
const GRAVITY = -7.0;
const DRAG = 0.92;                 // per-frame XZ velocity damping
const BASE_SIZE = 0.18;

const CONFETTI_PALETTE: number[] = [
  PALETTE.duck, PALETTE.duckBeak, PALETTE.policeRed, PALETTE.policeBlue,
  PALETTE.buildingC, PALETTE.buildingD, PALETTE.explosion,
];

interface Particle {
  vx: number; vy: number; vz: number;
  life: number;     // remaining seconds
  total: number;    // initial life — for fade interpolation
}

// ── Module state ──────────────────────────────────────────────────────────

let points: THREE.Points | null = null;
let geom: THREE.BufferGeometry | null = null;
let positions: Float32Array | null = null;
let colors: Float32Array | null = null;
let opacities: Float32Array | null = null;
const particles: Particle[] = [];
let writeIdx = 0;
const _color = new THREE.Color();

function ensurePool(world: World): void {
  if (points) return;
  positions = new Float32Array(MAX_PARTICLES * 3);
  colors = new Float32Array(MAX_PARTICLES * 3);
  opacities = new Float32Array(MAX_PARTICLES);
  for (let i = 0; i < MAX_PARTICLES; i++) particles.push({ vx: 0, vy: 0, vz: 0, life: 0, total: DEFAULT_LIFE });

  geom = new THREE.BufferGeometry();
  geom.setAttribute('position', new THREE.BufferAttribute(positions, 3));
  geom.setAttribute('color', new THREE.BufferAttribute(colors, 3));
  geom.setAttribute('alpha', new THREE.BufferAttribute(opacities, 1));

  // Tiny inline shader so per-particle alpha actually fades. PointsMaterial
  // doesn't support per-vertex opacity out of the box.
  const mat = new THREE.ShaderMaterial({
    transparent: true,
    depthWrite: false,
    blending: THREE.AdditiveBlending,
    vertexColors: true,
    uniforms: { uSize: { value: BASE_SIZE } },
    vertexShader: /* glsl */ `
      attribute float alpha;
      varying float vAlpha;
      varying vec3 vColor;
      uniform float uSize;
      void main() {
        vAlpha = alpha;
        vColor = color;
        vec4 mv = modelViewMatrix * vec4(position, 1.0);
        gl_PointSize = uSize * (300.0 / -mv.z);
        gl_Position = projectionMatrix * mv;
      }
    `,
    fragmentShader: /* glsl */ `
      varying float vAlpha;
      varying vec3 vColor;
      void main() {
        // Soft round particle.
        vec2 d = gl_PointCoord - 0.5;
        float r = length(d);
        if (r > 0.5) discard;
        float falloff = smoothstep(0.5, 0.0, r);
        gl_FragColor = vec4(vColor, vAlpha * falloff);
      }
    `,
  });

  points = new THREE.Points(geom, mat);
  points.frustumCulled = false; // bursts are short-lived; never bother culling
  world.scene.add(points);
}

/**
 * Spawn a `count`-particle burst at `pos`. Particles get random radial
 * velocity, an upward kick, and a random color from the confetti palette.
 */
export function triggerConfettiBurst(
  world: World,
  pos: THREE.Vector3,
  opts: { count?: number; speed?: number; up?: number; life?: number } = {},
): void {
  ensurePool(world);
  if (!positions || !colors || !opacities) return;

  const count = opts.count ?? 30;
  const speed = opts.speed ?? 4.0;
  const up = opts.up ?? 5.5;
  const life = opts.life ?? DEFAULT_LIFE;

  for (let i = 0; i < count; i++) {
    const idx = writeIdx;
    writeIdx = (writeIdx + 1) % MAX_PARTICLES;

    positions[idx * 3 + 0] = pos.x;
    positions[idx * 3 + 1] = pos.y;
    positions[idx * 3 + 2] = pos.z;

    // Random unit vector in XZ; bias upward.
    const ang = Math.random() * Math.PI * 2;
    const r = 0.4 + Math.random() * 0.8;
    const p = particles[idx]!;
    p.vx = Math.cos(ang) * speed * r;
    p.vz = Math.sin(ang) * speed * r;
    p.vy = up * (0.6 + Math.random() * 0.7);
    p.life = life;
    p.total = life;

    const c = CONFETTI_PALETTE[Math.floor(Math.random() * CONFETTI_PALETTE.length)]!;
    _color.setHex(c);
    colors[idx * 3 + 0] = _color.r;
    colors[idx * 3 + 1] = _color.g;
    colors[idx * 3 + 2] = _color.b;
    opacities[idx] = 1;
  }
  geom!.attributes['position']!.needsUpdate = true;
  geom!.attributes['color']!.needsUpdate = true;
  geom!.attributes['alpha']!.needsUpdate = true;
}

// ── System ────────────────────────────────────────────────────────────────

export const particleBurstSystem: System = {
  name: 'particleBurst',

  render(_dt: number, _alpha: number, world: World) {
    if (!positions || !opacities) return;
    const dt = world.time.lastFrame || 1 / 60;

    for (let i = 0; i < MAX_PARTICLES; i++) {
      const p = particles[i]!;
      if (p.life <= 0) continue;
      p.life -= dt;
      // Integrate position. Float32Array indexing is `number | undefined`
      // under noUncheckedIndexedAccess, so read-then-write explicitly.
      const ix = i * 3;
      positions[ix + 0] = (positions[ix + 0] ?? 0) + p.vx * dt;
      positions[ix + 1] = (positions[ix + 1] ?? 0) + p.vy * dt;
      positions[ix + 2] = (positions[ix + 2] ?? 0) + p.vz * dt;
      // Gravity + drag.
      p.vy += GRAVITY * dt;
      p.vx *= DRAG;
      p.vz *= DRAG;
      // Fade with remaining-life ratio (eased so most of the visible life
      // stays bright, then drops fast at the end).
      const t = Math.max(0, p.life / p.total);
      opacities[i] = t * t;
      if (p.life <= 0) opacities[i] = 0;
    }

    if (geom) {
      geom.attributes['position']!.needsUpdate = true;
      geom.attributes['alpha']!.needsUpdate = true;
    }
  },
};
